import type { Recipe } from '../types';

export function normalizeDistributionSignature(distribution?: Record<string, number>): string {
  if (!distribution) return '';
  return Object.entries(distribution)
    .filter(([, qty]) => Number.isFinite(qty) && qty > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, qty]) => `${id}:${Math.round(Number(qty) || 0)}`)
    .join('|');
}

// Both sizes of a recipe share its group, so a 400 g ↔ 800 g switch keeps the same
// recipe selected. Unknown ids group as themselves.
function recipeGroupOfProduct(recipes: Recipe[], productId: string): string {
  const recipe = recipes.find(
    (r) =>
      String(r.productId) === productId ||
      Object.values(r.variants || {}).some((variant) => String(variant.productId) === productId),
  );
  return recipe?.recipeGroup || (recipe ? String(recipe.productId) : productId);
}

export interface SelectedRecipesAfterPackEditArgs {
  selectedRecipes: string[];
  previousDistribution: Record<string, number> | undefined;
  nextDistribution: Record<string, number>;
  recipes: Recipe[];
}

/**
 * Selection to persist after the tutor edits the pack in the checkout modal.
 * Any change to `selectedRecipes` re-runs the plan calculation (which supersedes
 * the manual edit), so the selection only changes when a recipe was removed:
 * the removed recipe's ids are dropped and the current order is kept. Quantity
 * edits, reorders and size switches return `undefined` (leave it untouched).
 */
export function selectedRecipesAfterPackEdit({
  selectedRecipes,
  previousDistribution,
  nextDistribution,
  recipes,
}: SelectedRecipesAfterPackEditArgs): string[] | undefined {
  const groupOf = (productId: string) => recipeGroupOfProduct(recipes, productId);
  const groupsAfter = new Set(Object.keys(nextDistribution).map(groupOf));
  const removedGroups = new Set(
    Object.keys(previousDistribution || {})
      .map(groupOf)
      .filter((group) => !groupsAfter.has(group)),
  );
  if (removedGroups.size === 0) return undefined;
  return selectedRecipes.filter((productId) => !removedGroups.has(groupOf(productId)));
}

export interface DroppedRecipeRef {
  recipe_id: string;
  recipe_group: string;
}

/**
 * Selection minus the recipes the plan could not serve: by the dropped product id and
 * by its recipe group (a size sibling of a dropped recipe is dropped too). The same
 * array is returned when nothing was dropped so callers can skip a no-op update.
 */
export function selectedRecipesWithoutDropped(
  selectedRecipes: string[],
  dropped: DroppedRecipeRef[],
  recipes: Recipe[],
): string[] {
  if (dropped.length === 0) return selectedRecipes;
  const droppedIds = new Set(dropped.map((item) => item.recipe_id));
  const droppedGroups = new Set(dropped.map((item) => item.recipe_group));
  return selectedRecipes.filter(
    (productId) => !droppedIds.has(productId) && !droppedGroups.has(recipeGroupOfProduct(recipes, productId)),
  );
}

/** Monthly cans per product from a plan, for the pack editor's reserve (> 0 only). */
export function monthlyDistributionFromPlan(
  recipes: Array<{ recipe_id: string; monthly_cans: number }>,
): Record<string, number> {
  return Object.fromEntries(
    recipes.map((recipe) => [recipe.recipe_id, recipe.monthly_cans] as const).filter(([, quantity]) => quantity > 0),
  );
}

/** Catalog name of the recipe that owns a product id (base product or a size sibling), or
 *  undefined when the id is not in the catalog. `Recipe.id` is the handle, so callers must
 *  never compare it with a backend `recipe_id` (a product UUID). */
export function recipeNameForProductId(productId: string, recipes: Recipe[]): string | undefined {
  const owner = recipes.find(
    (recipe) =>
      String(recipe.productId) === productId ||
      Object.values(recipe.variants || {}).some((variant) => String(variant.productId) === productId),
  );
  return owner?.name;
}

/** Display names of dropped recipes: the catalog name of the recipe group, else the
 *  catalog name of the selected product (single-size recipes have no group), else the
 *  backend name. */
export function droppedRecipeNames(dropped: Array<DroppedRecipeRef & { name: string }>, recipes: Recipe[]): string[] {
  return dropped.map((item) => {
    const byGroup = item.recipe_group ? recipes.find((recipe) => recipe.recipeGroup === item.recipe_group) : undefined;
    const byProduct =
      byGroup ??
      recipes.find(
        (recipe) =>
          String(recipe.productId) === item.recipe_id ||
          Object.values(recipe.variants || {}).some((variant) => String(variant.productId) === item.recipe_id),
      );
    return byProduct?.name ?? item.name;
  });
}
